/**
 * Camera travel and stillness, from the XR view pose.
 *
 * Exists because depth relief decays while the camera is still and recovers on
 * motion (docs/MEASUREMENTS.md §4), so no relief reading means anything without
 * knowing how long the phone has been parked.
 */

/**
 * Below this a step is tracking jitter rather than the operator moving the
 * phone. Measured: a phone propped and untouched still accumulated ~3 mm/s of
 * pose drift, so a 5 mm gate restarted the stillness clock every couple of
 * seconds and no long run could ever be recorded. Restoring decayed relief
 * needs a 20–30 cm strafe, three orders above this, so the looser gate costs
 * nothing.
 */
const MOTION_EPSILON_METRES = 0.015

export class CameraMotion {
  private lastX = 0
  private lastY = 0
  private lastZ = 0
  private seeded = false
  private stillSince = 0

  /** Metres the camera has travelled in total. */
  travel = 0
  /** Whether the camera moved past the jitter floor on the last update. */
  moving = false

  reset(): void {
    this.seeded = false
    this.travel = 0
    this.moving = false
    this.stillSince = 0
  }

  /** `matrix` is view-to-world; its translation column is the camera position. */
  update(matrix: Float32Array, now: number): void {
    const x = matrix[12]!
    const y = matrix[13]!
    const z = matrix[14]!

    if (!this.seeded) {
      this.lastX = x
      this.lastY = y
      this.lastZ = z
      this.seeded = true
      this.stillSince = now
      return
    }

    // Deliberately not re-seeding on a sub-epsilon step: a slow drift then
    // accumulates against the last accepted position and still registers, where
    // per-frame differencing would discard it as jitter forever.
    const step = Math.hypot(x - this.lastX, y - this.lastY, z - this.lastZ)
    if (step <= MOTION_EPSILON_METRES) {
      this.moving = false
      return
    }

    this.travel += step
    this.lastX = x
    this.lastY = y
    this.lastZ = z
    this.stillSince = now
    this.moving = true
  }

  stillSeconds(now: number): number {
    return this.stillSince === 0 ? 0 : (now - this.stillSince) / 1000
  }
}
